/**
 * Fixed sets of values that cross an application boundary.
 *
 * These are wire values, not labels: each arrives from a query param or a
 * response body, so the string a member carries is fixed by whatever the
 * other side already sends and cannot be tidied. `SiteLocator` is lower-case
 * for exactly that reason while its neighbours are camelCase.
 */

/**
 * The `source` a downstream app declares when it hands a user off to the
 * Portal (`/?source=<value>&returnUrl=<url>`).
 *
 * Values read back are normalised case-insensitively (see
 * `normalizeExternalSource`) because the estate is inconsistent: Online Colo
 * has sent both `colo` and `onlinecolo`, and Site Locator sends `sitelocator`
 * where the newer apps send camelCase.
 */
export enum ExternalSource {
  Colo = 'colo',
  SiteLocator = 'sitelocator',
  VerticalIntel = 'verticalIntel',
  PartnerPortal = 'partnerPortal',
  VerticalWorks = 'verticalWorks',
}

/**
 * Legacy spellings still in circulation for an `ExternalSource`. Accepted on
 * read so an older caller keeps working; never emitted.
 */
export const EXTERNAL_SOURCE_ALIASES: Readonly<Record<string, ExternalSource>> = {
  onlinecolo: ExternalSource.Colo,
};

/**
 * `status` on the Vertical Works access contract, served by both
 * `GET /access/status` and `POST /access/request`.
 */
export enum VerticalWorksAccessStatus {
  Active = 'active',
  Pending = 'pending',
  NotProvisioned = 'notProvisioned',
  Disabled = 'disabled',
}

/**
 * `status` the Portal records for Partner Portal. PP itself sends only
 * `approved`; the Portal and the Auth0 Action derive this from it, so the
 * value must match the literal in `auth0-action.js`.
 */
export enum PartnerPortalAccessStatus {
  Pending = 'pending',
}

/**
 * The Colo database role that grants Site Locator access. It arrives in the
 * Colo user's `roles` (GetUser) and is matched case-insensitively.
 */
export const SITE_LOCATOR_ROLE = 'sitelocator-access';
